/**
 * User Management Component
 *
 * Admin/Manager interface for:
 * - Viewing all users
 * - Inviting new users
 * - Managing user roles
 * - Activating/deactivating users
 * - Viewing pending invitations
 */

import { useState, useEffect } from 'react'
import {
  UserPlus, Mail, Shield, ShieldCheck, Eye, Loader2,
  MoreVertical, Check, X, Clock, AlertCircle, Users,
  Building2, AlertTriangle, Globe
} from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../App'

// Role badge styles
const roleBadgeStyles = {
  admin: 'bg-violet-100 text-violet-700',
  manager: 'bg-blue-100 text-blue-700',
  viewer: 'bg-gray-100 text-gray-600'
}

// Role icons
const roleIcons = {
  admin: ShieldCheck,
  manager: Shield,
  viewer: Eye
}

export default function UserManagement() {
  const { user: currentUser } = useAuth()
  const [users, setUsers] = useState([])
  const [invitations, setInvitations] = useState([])
  const [facilities, setFacilities] = useState([])
  const [loading, setLoading] = useState(true)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteData, setInviteData] = useState({
    email: '',
    role: 'viewer',
    facility_ids: []
  })
  const [inviteError, setInviteError] = useState(null)
  const [inviting, setInviting] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const [showExternalConfirm, setShowExternalConfirm] = useState(false)

  // Facility access management
  const [showFacilityModal, setShowFacilityModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [userFacilities, setUserFacilities] = useState([])
  const [loadingFacilities, setLoadingFacilities] = useState(false)
  const [savingAccess, setSavingAccess] = useState(false)

  const isAdmin = currentUser?.role === 'admin'

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [usersRes, facilitiesRes] = await Promise.all([
        api.get('/users'),
        api.get('/facilities')
      ])
      setUsers(usersRes.users || [])
      setFacilities(facilitiesRes.facilities || [])

      // Only admins can see invitations
      if (isAdmin) {
        try {
          const invitesRes = await api.get('/users/invitations/list')
          setInvitations(invitesRes.invitations || [])
        } catch (err) {
          console.log('Could not fetch invitations')
        }
      }
    } catch (err) {
      console.error('Failed to fetch data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleInvite = async (e, confirmExternal = false) => {
    e?.preventDefault()
    setInviteError(null)
    setInviting(true)

    try {
      const payload = { ...inviteData }
      if (confirmExternal) {
        payload.confirm_external = true
      }

      const response = await api.post('/users/invite', payload)
      await fetchData()
      setShowInviteModal(false)
      setShowExternalConfirm(false)
      setInviteData({ email: '', role: 'viewer', facility_ids: [] })
    } catch (err) {
      // Check if this is an external email confirmation request
      // The API response data is stored in err.data by the API client
      if (err.data?.requires_confirmation && err.data?.is_external) {
        setShowExternalConfirm(true)
        setInviteError(null)
      } else {
        setInviteError(err.message || 'Failed to send invitation')
      }
    } finally {
      setInviting(false)
    }
  }

  const confirmExternalInvite = async () => {
    setInviting(true)
    try {
      await api.post('/users/invite', { ...inviteData, confirm_external: true })
      await fetchData()
      setShowInviteModal(false)
      setShowExternalConfirm(false)
      setInviteData({ email: '', role: 'viewer', facility_ids: [] })
    } catch (err) {
      setInviteError(err.message || 'Failed to send invitation')
    } finally {
      setInviting(false)
    }
  }

  const updateUserRole = async (userId, newRole) => {
    try {
      await api.put(`/users/${userId}/role`, { role: newRole })
      await fetchData()
    } catch (err) {
      alert('Failed to update role: ' + err.message)
    }
    setActiveDropdown(null)
  }

  const toggleUserStatus = async (userId, isActive) => {
    try {
      await api.put(`/users/${userId}/status`, { is_active: !isActive })
      await fetchData()
    } catch (err) {
      alert('Failed to update status: ' + err.message)
    }
    setActiveDropdown(null)
  }

  const revokeInvitation = async (inviteId) => {
    if (!confirm('Revoke this invitation?')) return
    try {
      await api.delete(`/users/invitations/${inviteId}`)
      await fetchData()
    } catch (err) {
      alert('Failed to revoke invitation: ' + err.message)
    }
  }

  const toggleFacilityAccess = (facilityId) => {
    setInviteData(prev => {
      const ids = prev.facility_ids || []
      const newIds = ids.includes(facilityId)
        ? ids.filter(id => id !== facilityId)
        : [...ids, facilityId]
      return { ...prev, facility_ids: newIds }
    })
  }

  // Open facility management modal for a user
  const openFacilityModal = async (user) => {
    setSelectedUser(user)
    setShowFacilityModal(true)
    setLoadingFacilities(true)
    setActiveDropdown(null)

    try {
      const res = await api.get(`/users/${user.id}`)
      setUserFacilities(res.facilities || [])
    } catch (err) {
      console.error('Failed to fetch user facilities:', err)
      setUserFacilities([])
    } finally {
      setLoadingFacilities(false)
    }
  }

  // Toggle facility access for a user
  const toggleUserFacilityAccess = async (facilityId, currentlyHasAccess) => {
    if (!selectedUser) return
    setSavingAccess(true)

    try {
      if (currentlyHasAccess) {
        // Revoke access
        await api.delete(`/users/${selectedUser.id}/facilities/${facilityId}`)
      } else {
        // Grant access
        await api.post(`/users/${selectedUser.id}/facilities`, {
          facility_id: facilityId,
          access_level: 'full'
        })
      }

      // Refresh user's facility list
      const res = await api.get(`/users/${selectedUser.id}`)
      setUserFacilities(res.facilities || [])
    } catch (err) {
      alert('Failed to update facility access: ' + err.message)
    } finally {
      setSavingAccess(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-500">Manage team access and invitations</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <UserPlus className="w-4 h-4" />
          Invite User
        </button>
      </div>

      {/* Users Table */}
      <div className="card p-0 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" />
            Team Members ({users.length})
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 text-left bg-gray-50">
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Last Login</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase w-10"></th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => {
                const RoleIcon = roleIcons[user.role] || Eye
                const isSelf = user.id === currentUser?.id

                return (
                  <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img
                            src={user.picture}
                            alt={user.name}
                            className="w-8 h-8 rounded-full"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-500">
                              {user.name?.[0] || user.email?.[0] || '?'}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">
                            {user.name || 'Unknown'}
                            {isSelf && <span className="text-gray-400 ml-1">(you)</span>}
                          </p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${roleBadgeStyles[user.role]}`}>
                          <RoleIcon className="w-3 h-3" />
                          {user.role}
                        </span>
                        {user.is_external && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700" title="External user (non-company email)">
                            <Globe className="w-3 h-3" />
                            External
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {user.is_active ? (
                        <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                          <Check className="w-4 h-4" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-gray-400 text-sm">
                          <X className="w-4 h-4" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {user.last_login
                        ? new Date(user.last_login).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="px-4 py-3">
                      {isAdmin && !isSelf && (
                        <div className="relative">
                          <button
                            onClick={() => setActiveDropdown(activeDropdown === user.id ? null : user.id)}
                            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>

                          {activeDropdown === user.id && (
                            <>
                              <div
                                className="fixed inset-0 z-10"
                                onClick={() => setActiveDropdown(null)}
                              />
                              <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                                <p className="px-3 py-1.5 text-xs text-gray-400 uppercase">Change Role</p>
                                {['admin', 'manager', 'viewer'].map(role => (
                                  <button
                                    key={role}
                                    onClick={() => updateUserRole(user.id, role)}
                                    disabled={user.role === role}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors
                                               ${user.role === role ? 'text-indigo-600' : 'text-gray-700'}`}
                                  >
                                    {role === user.role && <Check className="w-3 h-3 inline mr-2" />}
                                    {role.charAt(0).toUpperCase() + role.slice(1)}
                                  </button>
                                ))}
                                <div className="border-t border-gray-200 my-1" />
                                {user.role !== 'admin' && (
                                  <button
                                    onClick={() => openFacilityModal(user)}
                                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                                  >
                                    <Building2 className="w-3 h-3" />
                                    Manage Facilities
                                  </button>
                                )}
                                <button
                                  onClick={() => toggleUserStatus(user.id, user.is_active)}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                >
                                  {user.is_active ? 'Deactivate User' : 'Activate User'}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending Invitations */}
      {isAdmin && invitations.filter(i => i.status === 'pending').length > 0 && (
        <div className="card p-0 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              Pending Invitations ({invitations.filter(i => i.status === 'pending').length})
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {invitations.filter(i => i.status === 'pending').map(invite => (
              <div key={invite.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{invite.email}</p>
                    <p className="text-sm text-gray-500">
                      Invited as {invite.role} • Expires {new Date(invite.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => revokeInvitation(invite.id)}
                  className="text-sm text-red-600 hover:text-red-700 transition-colors"
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl border border-gray-200 shadow-2xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Invite User</h2>
              <button
                onClick={() => setShowInviteModal(false)}
                className="p-1 text-gray-400 hover:text-gray-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleInvite} className="p-4 space-y-4">
              {inviteError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{inviteError}</p>
                </div>
              )}

              <div>
                <label className="label">Email Address</label>
                <input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="user@storable.com"
                  className="input"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {isAdmin
                    ? 'External emails allowed with confirmation (restricted to viewer access)'
                    : 'Must be an @storable.com email address'}
                </p>
              </div>

              <div>
                <label className="label">Role</label>
                <div className="grid grid-cols-3 gap-2">
                  {['viewer', 'manager', 'admin'].map(role => {
                    const RoleIcon = roleIcons[role]
                    const isSelected = inviteData.role === role
                    // Managers can only invite viewers
                    const isDisabled = !isAdmin && role !== 'viewer'

                    return (
                      <button
                        key={role}
                        type="button"
                        onClick={() => !isDisabled && setInviteData(prev => ({ ...prev, role }))}
                        disabled={isDisabled}
                        className={`p-3 rounded-lg border text-center transition-colors
                                   ${isSelected
                                     ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                     : isDisabled
                                       ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                       : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'}`}
                      >
                        <RoleIcon className="w-5 h-5 mx-auto mb-1" />
                        <p className="text-xs font-medium capitalize">{role}</p>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Facility Access */}
              {facilities.length > 0 && (
                <div>
                  <label className="label">Facility Access</label>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
                    {facilities.filter(f => f.is_active).map(facility => (
                      <label
                        key={facility.id}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={inviteData.facility_ids.includes(facility.id)}
                          onChange={() => toggleFacilityAccess(facility.id)}
                          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">{facility.name}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Select facilities the user will have access to
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviting}
                  className="btn-primary flex items-center gap-2"
                >
                  {inviting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* External User Confirmation Modal */}
      {showExternalConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl border border-gray-200 shadow-2xl max-w-md w-full">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">External User Warning</h2>
                  <p className="text-sm text-gray-500">This user is outside the organization</p>
                </div>
              </div>
            </div>

            <div className="p-4 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 mb-3">
                  <strong>{inviteData.email}</strong> is not a @storable.com email address.
                </p>
                <p className="text-sm text-amber-700 mb-3">
                  External users will be:
                </p>
                <ul className="text-sm text-amber-700 space-y-1 ml-4 list-disc">
                  <li>Restricted to <strong>viewer</strong> access only</li>
                  <li>Marked as external in the user list</li>
                  <li>Required to sign in with Google using this exact email</li>
                </ul>
              </div>

              <p className="text-sm text-gray-600">
                Are you sure you want to invite this external user?
              </p>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowExternalConfirm(false)
                  setInviteError(null)
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmExternalInvite}
                disabled={inviting}
                className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              >
                {inviting && <Loader2 className="w-4 h-4 animate-spin" />}
                Yes, Invite External User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Facility Access Modal */}
      {showFacilityModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl border border-gray-200 shadow-2xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Manage Facility Access</h2>
                <p className="text-sm text-gray-500">{selectedUser.name || selectedUser.email}</p>
              </div>
              <button
                onClick={() => {
                  setShowFacilityModal(false)
                  setSelectedUser(null)
                }}
                className="p-1 text-gray-400 hover:text-gray-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Facility List */}
            <div className="p-4">
              {loadingFacilities ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-3">
                    {selectedUser.role === 'admin'
                      ? 'Admins have access to all facilities automatically.'
                      : 'Toggle which facilities this user can access:'}
                  </p>

                  {selectedUser.role === 'admin' ? (
                    <div className="p-4 rounded-lg bg-violet-50 border border-violet-200 text-center">
                      <ShieldCheck className="w-8 h-8 text-violet-500 mx-auto mb-2" />
                      <p className="text-sm text-violet-700">
                        Admin users have full access to all facilities
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {facilities.filter(f => f.is_active).map(facility => {
                        const hasAccess = userFacilities.some(uf => uf.id === facility.id)

                        return (
                          <div
                            key={facility.id}
                            className={`flex items-center justify-between p-3 rounded-lg border transition-colors
                                       ${hasAccess
                                         ? 'border-green-200 bg-green-50'
                                         : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                          >
                            <div className="flex items-center gap-3">
                              <Building2 className={`w-4 h-4 ${hasAccess ? 'text-green-600' : 'text-gray-400'}`} />
                              <span className="text-sm text-gray-700">{facility.name}</span>
                            </div>
                            <button
                              onClick={() => toggleUserFacilityAccess(facility.id, hasAccess)}
                              disabled={savingAccess}
                              className={`px-3 py-1 rounded text-xs font-medium transition-colors
                                         ${hasAccess
                                           ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                           : 'bg-green-100 text-green-600 hover:bg-green-200'}
                                         ${savingAccess ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {savingAccess ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : hasAccess ? (
                                'Revoke'
                              ) : (
                                'Grant'
                              )}
                            </button>
                          </div>
                        )
                      })}

                      {facilities.filter(f => f.is_active).length === 0 && (
                        <p className="text-center text-gray-500 py-4">
                          No facilities configured
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowFacilityModal(false)
                  setSelectedUser(null)
                }}
                className="btn-secondary"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
